/**
 * DM-8485 outreach-log presentation and the mail-merge sheet round trip.
 * Re-exported from websiteLeads.logic.ts, so imports and tests are unchanged.
 */

import { format, formatDistanceToNow } from 'date-fns';
import type {
  WebsiteLeadExport,
  WebsiteLeadOutreachEventType,
  WebsiteLeadOutreachFilter,
  WebsiteLeadOutreachImportResult,
  WebsiteLeadOutreachImportRow,
  WebsiteLeadOutreachImportSkipReason,
  WebsiteLeadOutreachStatus,
  WebsiteLeadOutreachSummary,
  WebsiteLeadType,
} from '../api/adminOps';
import type { ChipTone } from '../components/ui/StatusChip';
import type { ToastKind } from '../components/ui/Toast';

/** `some_value` -> `Some value` for options the maps do not know yet. */
function humanize(value: string): string {
  const spaced = value.replace(/_/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export const OUTREACH_FILTERS: readonly { value: WebsiteLeadOutreachFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'not_contacted', label: 'Not contacted' },
  { value: 'contacted_no_reply', label: 'Emailed, no reply' },
  { value: 'replied', label: 'Replied' },
  { value: 'booked', label: 'Call booked' },
  { value: 'opted_out', label: 'Opted out' },
  { value: 'bounced', label: 'Bounced' },
];

const OUTREACH_STATUS_CHIPS: Record<WebsiteLeadOutreachStatus, { label: string; tone: ChipTone }> =
  {
    not_contacted: { label: 'Not contacted', tone: 'neutral' },
    emailed: { label: 'Emailed', tone: 'info' },
    replied: { label: 'Replied', tone: 'warn' },
    booked: { label: 'Call booked', tone: 'ok' },
    held: { label: 'Call held', tone: 'ok' },
    opted_out: { label: 'Opted out', tone: 'crit' },
    bounced: { label: 'Bounced', tone: 'crit' },
  };

/** Where the lead stands; an emailed lead shows how many emails went out. */
export function outreachChip(summary: WebsiteLeadOutreachSummary): {
  label: string;
  tone: ChipTone;
} {
  const chips: Record<string, { label: string; tone: ChipTone }> = OUTREACH_STATUS_CHIPS;
  const chip = chips[summary.status] ?? { label: humanize(summary.status), tone: 'neutral' };
  return summary.status === 'emailed'
    ? { ...chip, label: `Emailed ×${summary.emails_sent}` }
    : chip;
}

/** `3 rows`, `1 row`. */
export function countNoun(count: number, noun: string): string {
  return `${count.toLocaleString()} ${noun}${count === 1 ? '' : 's'}`;
}

const ago = (iso: string): string => formatDistanceToNow(new Date(iso), { addSuffix: true });

/**
 * "2 emails · last emailed 3 days ago · andrew@donate-mate.com", or null
 * before any outreach. The recency is the newest email; a lead with only a
 * non-email event (a note, a bounce) shows that event's age instead.
 */
export function outreachSummaryLine(summary: WebsiteLeadOutreachSummary): string | null {
  const recency = summary.last_emailed_at
    ? `last emailed ${ago(summary.last_emailed_at)}`
    : summary.last_occurred_at
      ? `last ${ago(summary.last_occurred_at)}`
      : null;
  const parts = [
    summary.emails_sent > 0 ? countNoun(summary.emails_sent, 'email') : null,
    recency,
    summary.last_actor,
  ].filter((part): part is string => typeof part === 'string' && part !== '');
  return parts.length > 0 ? parts.join(' · ') : null;
}

export const OUTREACH_EVENT_TYPES: readonly WebsiteLeadOutreachEventType[] = [
  'email_sent',
  'replied',
  'call_booked',
  'call_held',
  'opted_out',
  'bounced',
  'note',
];

export const OUTREACH_EVENT_LABELS: Record<WebsiteLeadOutreachEventType, string> = {
  email_sent: 'Email sent',
  replied: 'Replied',
  call_booked: 'Call booked',
  call_held: 'Call held',
  opted_out: 'Opted out',
  bounced: 'Bounced',
  note: 'Note',
};

export function eventLabel(type: string): string {
  const labels: Record<string, string> = OUTREACH_EVENT_LABELS;
  return labels[type] ?? humanize(type);
}

/** What logging each event means, including the two side effects the backend applies. */
export const OUTREACH_EVENT_HINTS: Record<WebsiteLeadOutreachEventType, string> = {
  email_sent:
    "An email from a founder's own Gmail; the touch is its place in the sequence (1–4). The first email logged on a Founding Partner request moves it from New to Contacted.",
  replied: 'The lead wrote back. No more sequence emails to this lead.',
  call_booked: 'A call is on the calendar.',
  call_held: 'The call took place.',
  opted_out:
    'The lead asked us to stop. Marks an Early Access lead unsubscribed: no further email from this list.',
  bounced: 'The email could not be delivered; check the address before trying again.',
  note: 'Anything else worth remembering about this lead. Changes no status.',
};

export function eventHint(type: string): string | null {
  const hints: Record<string, string> = OUTREACH_EVENT_HINTS;
  return hints[type] ?? null;
}

/** Only a sent email has a place in the four-touch sequence. */
export function canHaveTouch(type: string): boolean {
  return type === 'email_sent';
}

export const OUTREACH_TOUCHES: readonly number[] = [1, 2, 3, 4];
export const OUTREACH_SUBJECT_MAX_LENGTH = 200;
export const OUTREACH_NOTE_MAX_LENGTH = 500;
export const OUTREACH_ACTOR_MAX_LENGTH = 254;
/** The import route takes this many rows per call. */
export const IMPORT_MAX_ROWS = 500;

const LOCAL_DATETIME_INPUT_FORMAT = "yyyy-MM-dd'T'HH:mm";

/** A Date as the value a datetime-local input takes (local time, minutes). */
export function toLocalDateTimeInput(date: Date): string {
  return format(date, LOCAL_DATETIME_INPUT_FORMAT);
}

/** A datetime-local value as ISO (UTC), or null when blank or unparseable. */
export function fromLocalDateTimeInput(value: string): string | null {
  if (value.trim() === '') return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

export interface SentLogError {
  readonly line: number;
  readonly message: string;
}

export interface ParsedSentLog {
  readonly lead_type: WebsiteLeadType;
  readonly rows: WebsiteLeadOutreachImportRow[];
  readonly errors: readonly SentLogError[];
}

type SentLogColumn = keyof WebsiteLeadOutreachImportRow;

/**
 * Header spellings the sheet may use, in priority order: the export carries
 * lead_id, Mailmeteor stamps a "Merge status" column, and people add their
 * own sent_at. Compared after headerKey(), so "Mail merge status" matches.
 */
const SENT_LOG_HEADERS: Record<SentLogColumn, readonly string[]> = {
  lead_id: ['lead_id', 'leadid', 'id'],
  email: ['email', 'email_address', 'e_mail', 'recipient'],
  occurred_at: [
    'occurred_at',
    'sent_at',
    'sent',
    'sent_on',
    'sent_date',
    'date_sent',
    'timestamp',
    'merge_status',
    'mail_merge_status',
    'mailmeteor_status',
  ],
  touch: ['touch', 'touch_number', 'step', 'sequence'],
  subject: ['subject', 'email_subject'],
  actor_label: ['actor', 'actor_label', 'sender', 'sent_by', 'sent_from', 'from'],
};

/** `Mail merge status` -> `mail_merge_status`. */
function headerKey(cell: string): string {
  return cell
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

interface SentLogRecord {
  /** The physical line the record starts on (1-based), as the sheet numbers rows. */
  readonly line: number;
  readonly cells: readonly string[];
  /** Nothing but whitespace: no cell, no delimiter, no quote. */
  readonly blank: boolean;
}

/**
 * The whole paste as records, RFC 4180 style: a quoted cell may hold the
 * delimiter, a line break or a doubled quote (a literal quote); a line break
 * outside quotes ends the record. The export quotes names, organizations and
 * subjects, which may well contain line breaks, so this walks the text with
 * quote state rather than splitting it into lines first. Cells come trimmed.
 */
function splitRecords(text: string, delimiter: string): SentLogRecord[] {
  const normalized = text.replace(/\r\n/g, '\n');
  const records: SentLogRecord[] = [];
  let cells: string[] = [];
  let current = '';
  let quoted = false;
  let hasContent = false;
  let line = 1;
  let startLine = 1;
  const endRecord = () => {
    cells.push(current.trim());
    records.push({ line: startLine, cells, blank: !hasContent });
    cells = [];
    current = '';
    hasContent = false;
  };
  for (let index = 0; index < normalized.length; index += 1) {
    const char = normalized[index];
    if (quoted) {
      if (char !== '"') {
        if (char === '\n') line += 1;
        current += char;
      } else if (normalized[index + 1] === '"') {
        current += '"';
        index += 1;
      } else {
        quoted = false;
      }
    } else if (char === '"' && current === '') {
      quoted = true;
      hasContent = true;
    } else if (char === delimiter) {
      cells.push(current.trim());
      current = '';
      hasContent = true;
    } else if (char === '\n') {
      endRecord();
      line += 1;
      startLine = line;
    } else {
      if (!/\s/.test(char)) hasContent = true;
      current += char;
    }
  }
  endRecord();
  return records;
}

/** A calendar date is present: ISO, US slashes, or a month name with a year. */
const LOOKS_LIKE_DATE =
  /\d{4}-\d{1,2}-\d{1,2}|\d{1,2}\/\d{1,2}\/\d{4}|\d{4}\/\d{1,2}\/\d{1,2}|[A-Za-z]{3,}\.? \d{1,2},? \d{4}|\d{1,2} [A-Za-z]{3,}\.? \d{4}/;

/**
 * A leading mail-merge status word and what follows it: `EMAIL_SENT
 * 10/20/2026 14:32`, `SENT on 2026-10-20`, `BOUNCED: 10/20/2026`. The word
 * is capitals and underscores only, so a month name never reads as a status.
 */
const STATUS_PREFIX = /^([A-Z][A-Z_]*)(?![a-z0-9])(?:\s+(?:on|at)\b)?\s*[:-]?\s*(.*)$/s;

/**
 * The mail-merge statuses that mean the email went out. Opened, clicked and
 * replied imply sent. Anything else before the date (BOUNCED, EMAIL_ERROR,
 * NOT_SENT, UNSUBSCRIBED, …) means the row must not be logged as email_sent.
 */
export const SENT_STATUSES: readonly string[] = [
  'EMAIL_SENT',
  'SENT',
  'EMAIL_OPENED',
  'OPENED',
  'EMAIL_CLICKED',
  'CLICKED',
  'EMAIL_REPLIED',
  'REPLIED',
];

export type SheetTimestamp =
  | { readonly kind: 'sent'; readonly iso: string }
  | { readonly kind: 'not_sent'; readonly status: string }
  | { readonly kind: 'unreadable' };

const readSheetDate = (candidate: string): SheetTimestamp => {
  if (candidate === '' || !LOOKS_LIKE_DATE.test(candidate)) return { kind: 'unreadable' };
  const parsed = new Date(candidate);
  return Number.isNaN(parsed.getTime())
    ? { kind: 'unreadable' }
    : { kind: 'sent', iso: parsed.toISOString() };
};

/**
 * A sheet cell as a sent time. A plain timestamp (ISO, `10/20/2026 14:32`,
 * `Oct 20, 2026`) is sent. A status word before the date is sent only when
 * it is one of SENT_STATUSES; any other status is `not_sent`, carrying the
 * word for the error line. A bare number or status word alone is unreadable.
 */
export function parseSheetTimestamp(raw: string): SheetTimestamp {
  const trimmed = raw.trim();
  const prefixed = STATUS_PREFIX.exec(trimmed);
  if (prefixed && LOOKS_LIKE_DATE.test(prefixed[2])) {
    const status = prefixed[1];
    return SENT_STATUSES.includes(status)
      ? readSheetDate(prefixed[2])
      : { kind: 'not_sent', status };
  }
  return readSheetDate(trimmed);
}

/**
 * The pasted sheet (header row first, CSV or tab separated) as import rows.
 * Blank records are skipped; a row without lead_id or email, without a
 * readable sent timestamp, or stamped with a status that is not a sent
 * status is reported by the line it starts on and left out.
 */
export function parseSentLog(text: string, leadType: WebsiteLeadType): ParsedSentLog {
  const rows: WebsiteLeadOutreachImportRow[] = [];
  const errors: SentLogError[] = [];
  // The delimiter comes from the header's first physical line: a tab means TSV.
  const firstLine = text.split(/\r?\n/).find(line => line.trim() !== '');
  if (firstLine === undefined) return { lead_type: leadType, rows, errors };
  const delimiter = firstLine.includes('\t') ? '\t' : ',';
  const [header, ...body] = splitRecords(text, delimiter).filter(record => !record.blank);
  if (header === undefined) return { lead_type: leadType, rows, errors };

  const headers = header.cells.map(headerKey);
  const columnIndex = (column: SentLogColumn): number => {
    for (const alias of SENT_LOG_HEADERS[column]) {
      const index = headers.indexOf(alias);
      if (index !== -1) return index;
    }
    return -1;
  };
  const columns = {
    lead_id: columnIndex('lead_id'),
    email: columnIndex('email'),
    occurred_at: columnIndex('occurred_at'),
    touch: columnIndex('touch'),
    subject: columnIndex('subject'),
    actor_label: columnIndex('actor_label'),
  };
  if (columns.lead_id === -1 && columns.email === -1) {
    errors.push({ line: header.line, message: 'The header needs a lead_id or email column.' });
  }
  if (columns.occurred_at === -1) {
    errors.push({
      line: header.line,
      message: 'The header needs a sent timestamp column (occurred_at, sent_at or Merge status).',
    });
  }
  if (errors.length > 0) return { lead_type: leadType, rows, errors };

  const cellAt = (cells: readonly string[], index: number): string =>
    index === -1 ? '' : (cells[index] ?? '');

  for (const { line: lineNumber, cells } of body) {
    const leadId = cellAt(cells, columns.lead_id);
    const email = cellAt(cells, columns.email).toLowerCase();
    if (leadId === '' && email === '') {
      errors.push({ line: lineNumber, message: 'No lead_id or email.' });
      continue;
    }
    const rawTimestamp = cellAt(cells, columns.occurred_at);
    if (rawTimestamp === '') {
      errors.push({ line: lineNumber, message: 'No sent timestamp (not sent yet?).' });
      continue;
    }
    const stamp = parseSheetTimestamp(rawTimestamp);
    if (stamp.kind === 'not_sent') {
      errors.push({
        line: lineNumber,
        message: `Status "${stamp.status}" is not a sent status; row skipped.`,
      });
      continue;
    }
    if (stamp.kind === 'unreadable') {
      errors.push({ line: lineNumber, message: `Unreadable timestamp "${rawTimestamp}".` });
      continue;
    }
    const rawTouch = cellAt(cells, columns.touch);
    const touch = rawTouch === '' ? null : Number(rawTouch);
    if (touch !== null && !OUTREACH_TOUCHES.includes(touch)) {
      errors.push({ line: lineNumber, message: `Touch must be 1–4, not "${rawTouch}".` });
      continue;
    }
    const subject = cellAt(cells, columns.subject);
    const actor = cellAt(cells, columns.actor_label);
    rows.push({
      ...(leadId !== '' ? { lead_id: leadId } : {}),
      ...(email !== '' ? { email } : {}),
      occurred_at: stamp.iso,
      ...(touch !== null ? { touch } : {}),
      ...(subject !== '' ? { subject } : {}),
      ...(actor !== '' ? { actor_label: actor } : {}),
    });
  }
  return { lead_type: leadType, rows, errors };
}

/** "3 rows parsed, 1 error" for the import preview. */
export function sentLogPreviewLine(parsed: {
  rows: readonly unknown[];
  errors: readonly unknown[];
}): string {
  return `${countNoun(parsed.rows.length, 'row')} parsed, ${countNoun(parsed.errors.length, 'error')}`;
}

const SKIP_REASONS: readonly WebsiteLeadOutreachImportSkipReason[] = [
  'no_match',
  'ambiguous',
  'not_found',
  'already_logged',
  'mismatch',
];

const SKIP_REASON_LABELS: Record<WebsiteLeadOutreachImportSkipReason, string> = {
  no_match: 'no match',
  ambiguous: 'ambiguous',
  not_found: 'not found',
  already_logged: 'already logged',
  mismatch: 'id and email disagree',
};

/** "Appended 12 of 14 rows; skipped: 1 no match, 1 already logged". */
export function importResultLine(result: WebsiteLeadOutreachImportResult): string {
  const head = `Appended ${result.appended} of ${countNoun(result.received, 'row')}`;
  if (result.skipped.length === 0) return head;
  const counts = new Map<string, number>();
  for (const skip of result.skipped) counts.set(skip.reason, (counts.get(skip.reason) ?? 0) + 1);
  const labels: Record<string, string> = SKIP_REASON_LABELS;
  const ordered = [...SKIP_REASONS, ...counts.keys()].filter(
    (reason, index, all) => counts.has(reason) && all.indexOf(reason) === index
  );
  const detail = ordered
    .map(reason => `${counts.get(reason)} ${labels[reason] ?? humanize(reason).toLowerCase()}`)
    .join(', ');
  return `${head}; skipped: ${detail}`;
}

/** The export toast: the count, and a warning when the server cut the list short. */
export function exportResultMessage(result: WebsiteLeadExport): {
  kind: ToastKind;
  message: string;
} {
  const recipients = countNoun(result.row_count, 'recipient');
  return result.truncated
    ? {
        kind: 'error',
        message: `Exported only the first ${recipients} to ${result.filename}; narrow the filters to export the rest.`,
      }
    : { kind: 'success', message: `Exported ${recipients} to ${result.filename}` };
}

const DEFAULT_EXPORT_FILENAME = 'website-leads.csv';

/** The server's filename as a safe basename ending in .csv (no paths, no odd characters). */
export function csvDownloadName(filename: string): string {
  const base = filename.split(/[\\/]/).pop() ?? '';
  const safe = base.replace(/[^A-Za-z0-9._-]+/g, '-').replace(/^[-.]+/, '');
  if (safe === '') return DEFAULT_EXPORT_FILENAME;
  return /\.csv$/i.test(safe) ? safe : `${safe}.csv`;
}

/**
 * Hands the CSV to the browser as a file download. Returns false where there
 * is no document or object URLs (the node test environment).
 */
export function triggerCsvDownload(filename: string, csv: string): boolean {
  if (typeof document === 'undefined' || typeof URL.createObjectURL !== 'function') return false;
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = csvDownloadName(filename);
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
  return true;
}
